/**
 * When the tenant-server rejects or cannot resolve the portal session, we sign the user out
 * of Auth0 and return them to the login screen. Impersonation routes opt out (admin JWT).
 */

export type TenantPortalAuthFailureReason =
  | 'http_unauthorized'
  | 'http_forbidden'
  | 'http_service_unavailable'
  | 'access_token_unavailable'
  | 'profile_load_failed'
  | 'missing_portal_identity';

const PORTAL_AUTH_ERROR_CODES = new Set([
  'UNAUTHORIZED',
  'IDENTITY_NOT_RESOLVED',
  'TENANT_NOT_FOUND',
  'AUTH_NOT_CONFIGURED',
]);

export function isPortalAuthFailureResponse(status: number, errorCode?: string | null): boolean {
  if (status === 401) return true;
  if (status === 503 && errorCode === 'AUTH_NOT_CONFIGURED') return true;
  if (status === 403 && errorCode && PORTAL_AUTH_ERROR_CODES.has(errorCode)) return true;
  return false;
}

export function shouldSkipPortalSessionInvalidation(endpoint: string): boolean {
  return endpoint.includes('/tenant/login-event');
}

export function isImpersonationPortalRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/impersonate');
}

type AuthFailureHandler = (reason: TenantPortalAuthFailureReason) => void;

let authFailureHandler: AuthFailureHandler | null = null;
let invalidationInProgress = false;

export function registerTenantPortalAuthFailureHandler(handler: AuthFailureHandler | null): void {
  authFailureHandler = handler;
}

export function isPortalSessionInvalidating(): boolean {
  return invalidationInProgress;
}

export function notifyTenantPortalAuthFailure(reason: TenantPortalAuthFailureReason): void {
  if (invalidationInProgress) return;
  if (isImpersonationPortalRoute()) return;
  invalidationInProgress = true;
  authFailureHandler?.(reason);
}

/** For tests — reset module state. */
export function resetTenantPortalSessionStateForTests(): void {
  invalidationInProgress = false;
  authFailureHandler = null;
}
